import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Bullet } from './Bullet';
import { makeGlowSprite } from './energyGlow';
import { useStore } from '../../state/store';

/**
 * Aether Dreadnought — sky battleship-of-the-line with broadside batteries.
 * Cruises in a slow circle around the player. Every ~6 seconds it fires a synchronized
 * salvo from one side (alternating port/starboard). Each broadside cannon is hittable
 * and destroyable, but the only kill point is the conning tower amidships.
 */
const TOWER_HP = 50;
const GUN_HP = 8;
const GUNS_PER_SIDE = 4;
const SALVO_INTERVAL = 6.0;
const SALVO_SPACING = 0.10; // delay between successive shells in a salvo
const ALTITUDE = 380;
const ORBIT_RADIUS = 1900;
const SPEED = 14;
const SCORE = 6500;

const HULL_HALF_W = 12;
const HULL_HALF_L = 48;
const HULL_HEIGHT = 6;

interface BroadsideGun {
  id: string;
  group: THREE.Group;
  cannon: THREE.Object3D;
  side: 'port' | 'starboard';
  hp: number;
  destroyed: boolean;
}

type Stage = 'cruising' | 'falling' | 'done';

export class AetherDreadnought extends Entity {
  stage: Stage = 'cruising';
  forward = new THREE.Vector3(0, 0, -1);
  readonly speed = SPEED;
  private orbitAngle = 0;
  private salvoTimer = 4.0;
  private salvoSide: 'port' | 'starboard' = 'port';
  /** Shots queued for staggered fire on game time (no setTimeout). */
  private pendingShots: { gun: BroadsideGun; at: number }[] = [];
  private guns: BroadsideGun[] = [];
  private tower: THREE.Mesh;
  private towerHp = Math.round(TOWER_HP * bossHpScale()); // later waves field tougher dreadnoughts
  private towerHpMax = this.towerHp;
  /** Sim-time until which the tower's heat vents are open (set by each broadside). */
  private ventUntil = -999;
  private towerBaseColor: THREE.Color | null = null;
  private envelopes: THREE.Object3D[] = [];
  private fallVel = new THREE.Vector3();
  private fallAng = new THREE.Vector3();
  private dieTimer = 0;
  private smokeTimer = 0;

  constructor() {
    super('zeppelin');
    this.hp = 999;
    this.radius = 60;
    this.orbitAngle = Math.random() * Math.PI * 2;
    this.group.position.set(
      Math.cos(this.orbitAngle) * ORBIT_RADIUS,
      ALTITUDE,
      Math.sin(this.orbitAngle) * ORBIT_RADIUS,
    );

    const built = makeDreadnoughtMesh();
    this.group.add(built.mesh);
    this.tower = built.tower;
    this.guns = built.guns;
    for (const p of this.guns) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts
    this.envelopes = built.envelopes;

    // Only the tower (kill point) and broadside guns are damageable. Bullets that miss
    // these slip past the dreadnought rather than getting absorbed by a hull catch-all.
    this.hitParts = [
      { id: 'tower', box: new THREE.Box3(new THREE.Vector3(-3, -3, -3), new THREE.Vector3(3, 4, 3)), object: this.tower },
      ...this.guns.map((g) => ({
        id: g.id,
        box: new THREE.Box3(new THREE.Vector3(-2, -1.5, -2), new THREE.Vector3(2, 2.5, 2)),
        object: g.group,
      })),
    ];
  }

  alive(): boolean { return this.stage === 'cruising'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'cruising') return;
    if (partId === 'tower') {
      // RHYTHM: the tower's armor only vents heat during/after a broadside — damage it in the
      // window each salvo opens; dodging the salvo earns the attack run.
      if (ctx.now > this.ventUntil) {
        const wp = new THREE.Vector3();
        this.tower.getWorldPosition(wp);
        ctx.game.shieldedFeedback(ctx, wp, 'ARMORED — THE TOWER VENTS AFTER EACH BROADSIDE');
        return;
      }
      this.towerHp -= amount;
      const wp = new THREE.Vector3();
      this.tower.getWorldPosition(wp);
      new Explosion(wp, 6, 0.3, 'fire').addTo(ctx);
      if (this.towerHp <= 0) this.beginDying(ctx);
      return;
    }
    // (No hull part — misses pass through.)
    const gun = this.guns.find((gg) => gg.id === partId);
    if (gun && !gun.destroyed) {
      gun.hp -= amount;
      const wp = new THREE.Vector3();
      gun.group.getWorldPosition(wp);
      new Explosion(wp, 4, 0.2, 'fire').addTo(ctx);
      if (gun.hp <= 0) {
        gun.destroyed = true;
        new Explosion(wp, 18, 0.6, 'fire').addTo(ctx);
        new Explosion(wp, 14, 3, 'smoke').setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
        ctx.game.audio.playBoom(0.5);
        if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== gun.id);
        // Visual: tilt + darken
        gun.group.rotation.z = (Math.random() < 0.5 ? -1 : 1) * 0.4;
        gun.group.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) {
            const mat = m.material as THREE.MeshStandardMaterial;
            mat.color?.setHex(0x1c1814);
          }
        });
      }
    }
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'falling') { this.updateFalling(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Cruise
    this.orbitAngle += (SPEED / ORBIT_RADIUS) * dt;
    this.group.position.set(
      Math.cos(this.orbitAngle) * ORBIT_RADIUS,
      ALTITUDE,
      Math.sin(this.orbitAngle) * ORBIT_RADIUS,
    );
    const tangentAngle = this.orbitAngle + Math.PI / 2;
    this.group.rotation.set(0, -tangentAngle, 0);
    this.forward.set(Math.cos(tangentAngle), 0, Math.sin(tangentAngle));

    // Salvo timer — fires synchronized broadside on alternating sides. ENRAGE at half tower HP:
    // BOTH broadsides at once and a faster cycle — more danger, but vent windows come quicker too.
    const enraged = this.towerHp <= this.towerHpMax / 2;
    this.salvoTimer -= dt;
    if (this.salvoTimer <= 0) {
      this.salvoTimer = enraged ? SALVO_INTERVAL * 0.7 : SALVO_INTERVAL;
      this.queueBroadside(this.salvoSide);
      if (enraged) this.queueBroadside(this.salvoSide === 'port' ? 'starboard' : 'port');
      this.salvoSide = this.salvoSide === 'port' ? 'starboard' : 'port';
      // Open the tower's heat vents — the punish window for this salvo.
      this.ventUntil = ctx.now + 3.5;
    }
    // Telegraph the window: the tower glows hot while it vents, cools when armored.
    const towerMat = this.tower.material as THREE.MeshStandardMaterial;
    if (towerMat && towerMat.color) {
      if (!this.towerBaseColor) this.towerBaseColor = towerMat.color.clone();
      if (ctx.now <= this.ventUntil) towerMat.color.setHex(0xd96a35);
      else towerMat.color.copy(this.towerBaseColor);
    }
    // Fire queued shots on GAME time (not setTimeout/wall-clock, so pause + frame
    // stutter don't desync the salvo from the simulation).
    if (this.pendingShots.length) {
      for (let i = this.pendingShots.length - 1; i >= 0; i--) {
        this.pendingShots[i].at -= dt;
        if (this.pendingShots[i].at <= 0) {
          const gun = this.pendingShots[i].gun;
          if (!gun.destroyed) this.fireGun(ctx, gun);
          this.pendingShots.splice(i, 1);
        }
      }
    }
  }

  private queueBroadside(side: 'port' | 'starboard') {
    const sideGuns = this.guns.filter((g) => g.side === side && !g.destroyed);
    sideGuns.forEach((g, i) => {
      this.pendingShots.push({ gun: g, at: i * SALVO_SPACING });
    });
  }

  private fireGun(ctx: EntityCtx, gun: BroadsideGun) {
    const muzzle = new THREE.Vector3();
    gun.cannon.getWorldPosition(muzzle);
    const player = ctx.game.player.position;
    const dir = player.clone().sub(muzzle).normalize();
    // Add a healthy spread so broadsides feel like volume, not lasers.
    dir.x += (Math.random() - 0.5) * 0.06;
    dir.y += (Math.random() - 0.5) * 0.06;
    dir.z += (Math.random() - 0.5) * 0.06;
    dir.normalize();
    const vel = dir.multiplyScalar(220);
    ctx.game.add(new Bullet(muzzle, vel, this.id, 'enemy'));
    new Explosion(muzzle.clone(), 4, 0.18, 'fire').addTo(ctx);
    new Explosion(muzzle.clone(), 5, 0.6, 'smoke').setDrift(new THREE.Vector3(0, 4, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.4);
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'falling';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    ctx.game.audio.playHydrogenRoar();
    // Initial keel-snap explosion at the conning tower.
    const tw = new THREE.Vector3();
    this.tower.getWorldPosition(tw);
    new Explosion(tw, 80, 1.0, 'fire').addTo(ctx);
    // Random angular vel — uneven envelope loss
    this.fallAng.set((Math.random() - 0.5) * 0.6, 0, 0.4 + Math.random() * 0.4);
    this.fallVel.set(0, -6, 0);
  }

  private updateFalling(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    this.fallVel.y -= 14 * ctx.dt;
    this.group.position.addScaledVector(this.fallVel, ctx.dt);
    // Apply angular velocity
    const omega = this.fallAng.clone().multiplyScalar(ctx.dt);
    const m = omega.length();
    if (m > 1e-6) {
      const ax = omega.divideScalar(m);
      const dq = new THREE.Quaternion().setFromAxisAngle(ax, m);
      this.group.quaternion.premultiply(dq);
    }
    // Continuous fire & smoke from envelopes
    this.smokeTimer += ctx.dt;
    if (this.smokeTimer >= 0.06) {
      this.smokeTimer = 0;
      // Random envelope-area explosion
      const envIdx = Math.floor(Math.random() * this.envelopes.length);
      const env = this.envelopes[envIdx];
      const ep = new THREE.Vector3();
      env.getWorldPosition(ep);
      new Explosion(ep, 18, 2.8, 'smoke').setDrift(new THREE.Vector3(0, 10, 0)).addTo(ctx);
      if (Math.random() < 0.4) new Explosion(ep, 8, 0.5, 'fire').addTo(ctx);
    }
    if (this.group.position.y <= -460) {
      // Massive impact
      const center = this.group.position.clone();
      center.y = -460;
      new Explosion(center, 240, 1.3, 'fire').addTo(ctx);
      new Explosion(center, 160, 0.8, 'fire').addTo(ctx);
      for (let i = 0; i < 6; i++) {
        const off = new THREE.Vector3((Math.random() - 0.5) * 80, 4, (Math.random() - 0.5) * 80);
        new Explosion(center.clone().add(off), 80, 7, 'smoke')
          .setDrift(new THREE.Vector3((Math.random() - 0.5) * 3, 14, (Math.random() - 0.5) * 3))
          .addTo(ctx);
      }
      ctx.game.audio.playBoom(1.6);
      // The falling battleship obliterates the ground it lands on for a wide radius.
      const groundCenter = center.clone().setY(ctx.game.gameScene.heightAt(center.x, center.z));
      ctx.game.crashImpact(ctx, groundCenter, 170, 30, this);
      this.stage = 'done';
    }
  }
}

/* ---------------------- mesh ---------------------- */

interface DBuilt { mesh: THREE.Group; tower: THREE.Mesh; guns: BroadsideGun[]; envelopes: THREE.Object3D[]; }

function makeDreadnoughtMesh(): DBuilt {
  const g = new THREE.Group();
  const guns: BroadsideGun[] = [];
  const envelopes: THREE.Object3D[] = [];

  const iron = new THREE.MeshStandardMaterial({ color: 0x4a4f55, roughness: 0.7, metalness: 0.45 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x2a2f33, roughness: 0.8, metalness: 0.3 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb88a30, roughness: 0.5, metalness: 0.8 });
  const fabric = new THREE.MeshStandardMaterial({ color: 0xa8a087, roughness: 0.95 });
  const glow = new THREE.MeshBasicMaterial({ color: 0xffd28a });
  const black = new THREE.MeshStandardMaterial({ color: 0x121212, roughness: 0.5, metalness: 0.6 });

  // Hull: long iron slab with stepped upper deck.
  const hull = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 2, HULL_HEIGHT, HULL_HALF_L * 2, 6, 6, 18), iron);
  hull.position.set(0, 0, 0);
  g.add(hull);
  const upperDeck = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 1.6, 2, HULL_HALF_L * 1.6, 6, 2, 12), ironDark);
  upperDeck.position.set(0, HULL_HEIGHT * 0.5 + 1, 0);
  g.add(upperDeck);
  // Bow ram (sloped front)
  const bow = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 1.7, HULL_HEIGHT, 6, 4, 4, 2), iron);
  bow.rotation.x = 0.3;
  bow.position.set(0, -1.8, -HULL_HALF_L - 1.8);
  g.add(bow);
  // Stern hump
  const stern = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 1.5, 4, 6, 4, 3, 2), iron);
  stern.position.set(0, 1, HULL_HALF_L - 0.5);
  g.add(stern);

  // 4 gas envelopes along the spine — fabric-doped ovoids floating above the hull.
  const envelopeMat = fabric;
  for (let i = 0; i < 4; i++) {
    const env = new THREE.Mesh(new THREE.SphereGeometry(5.5, 40, 24), envelopeMat);
    env.scale.set(2.3, 1, 1.4);
    env.position.set(0, HULL_HEIGHT + 7.5, -HULL_HALF_L + 12 + i * 24);
    g.add(env);
    envelopes.push(env);
    // Cable struts from envelope to deck
    for (const sx of [-3, 3]) {
      const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 6, 8), ironDark);
      cable.position.set(sx, HULL_HEIGHT + 4.5, -HULL_HALF_L + 12 + i * 24);
      g.add(cable);
    }
  }

  // Conning tower amidships — glowing bridge windows
  const towerStem = new THREE.Mesh(new THREE.BoxGeometry(6, 5, 8, 4, 4, 4), iron);
  towerStem.position.set(0, HULL_HEIGHT * 0.5 + 4.5, 0);
  g.add(towerStem);
  const towerCore = new THREE.Mesh(new THREE.BoxGeometry(4.5, 3, 4.5, 4, 3, 4), brass);
  towerCore.position.set(0, HULL_HEIGHT * 0.5 + 9, 0);
  towerCore.add(makeGlowSprite(0xffd28a, 10));
  g.add(towerCore);
  // Glowing window strip
  for (const sz of [-1.7, 0, 1.7]) {
    const w = new THREE.Mesh(new THREE.PlaneGeometry(3.8, 0.8), glow);
    w.position.set(0, HULL_HEIGHT * 0.5 + 9, sz === 0 ? 2.3 : 0);
    if (sz === 0) {
      // front window
    } else {
      w.rotation.y = sz > 0 ? Math.PI / 2 : -Math.PI / 2;
      w.position.set(sz > 0 ? 2.3 : -2.3, HULL_HEIGHT * 0.5 + 9, 0);
    }
    g.add(w);
  }
  // Funnels behind the tower
  for (const sx of [-2.6, 2.6]) {
    const funnel = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.85, 4, 16, 2), ironDark);
    funnel.position.set(sx, HULL_HEIGHT * 0.5 + 8, 7);
    g.add(funnel);
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.3, 16), brass);
    band.position.set(sx, HULL_HEIGHT * 0.5 + 9, 7);
    g.add(band);
  }

  // 4 broadside guns per side (port = -X, starboard = +X)
  for (let i = 0; i < GUNS_PER_SIDE; i++) {
    const z = -HULL_HALF_L + 12 + i * 24;
    for (const side of ['port', 'starboard'] as const) {
      const sx = side === 'port' ? -HULL_HALF_W - 0.3 : HULL_HALF_W + 0.3;
      const gunGroup = new THREE.Group();
      // Barbette
      const barb = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 1.9, 1.4, 24), iron);
      barb.position.set(0, 0, 0);
      gunGroup.add(barb);
      // Turret box facing outward (along X for the gun barrel)
      const turret = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.5, 2.0, 4, 3, 4), iron);
      turret.position.set(0, 1.4, 0);
      gunGroup.add(turret);
      // Cannon barrels — twin barrels facing outward (sx direction)
      for (const dz of [-0.4, 0.4]) {
        const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 3.2, 16), black);
        barrel.rotation.z = Math.PI / 2;
        barrel.position.set(side === 'port' ? -1.9 : 1.9, 1.4, dz);
        gunGroup.add(barrel);
      }
      // Cannon reference for muzzle world-pos
      const cannonRef = new THREE.Object3D();
      cannonRef.position.set(side === 'port' ? -3.4 : 3.4, 1.4, 0);
      gunGroup.add(cannonRef);
      gunGroup.position.set(sx, 1.5, z);
      g.add(gunGroup);
      const id = `${side}${i}`;
      guns.push({ id, group: gunGroup, cannon: cannonRef, side, hp: GUN_HP, destroyed: false });
    }
  }

  // Underbelly running lights
  for (let i = -2; i <= 2; i++) {
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.35, 16, 12), glow);
    lamp.position.set(0, -HULL_HEIGHT - 0.4, i * 14);
    g.add(lamp);
  }

  return { mesh: g, tower: towerCore, guns, envelopes };
}
